<template>
  <v-dialog :value="true" max-width="600" persistent scrollable>
    <v-card>
      <v-card-title>Nómina — {{ obligation.name }}</v-card-title>
      <v-card-subtitle class="pt-1">
        Tilde quiénes figuran en el documento. Los activos que queden sin tildar
        se informan uno por uno en los correos.
      </v-card-subtitle>
      <v-card-text>
        <v-alert v-if="lockMessage" type="warning" dense text>{{ lockMessage }}</v-alert>
        <div v-for="section in sections" :key="section.kind" class="payroll-section">
          <div class="d-flex align-center">
            <div class="subtitle-2">{{ section.title }}</div>
            <v-spacer />
            <v-btn
              v-if="section.active.length && !lockMessage"
              x-small
              text
              color="primary"
              :disabled="saving"
              @click="selectAll(section, true)"
            >Todos</v-btn>
            <v-btn
              v-if="section.active.length && !lockMessage"
              x-small
              text
              :disabled="saving"
              @click="selectAll(section, false)"
            >Ninguno</v-btn>
          </div>
          <div v-if="!section.active.length && !section.excludedHere.length && !section.inactive.length" class="caption grey--text">
            {{ section.empty }}
          </div>
          <v-checkbox
            v-for="member in section.active"
            :key="member._id"
            v-model="selected"
            :value="member._id"
            :label="section.labelOf(member)"
            :disabled="saving || !!lockMessage"
            hide-details
            dense
          />
          <!-- Excluidos solo de esta obligacion ("Excluir obligaciones de
               contratista" en el personal): igual que los no activos, con aviso. -->
          <div v-for="member in section.excludedHere" :key="member._id">
            <v-checkbox
              v-model="selected"
              :value="member._id"
              :label="section.labelOf(member)"
              class="payroll-member--inactive"
              disabled
              hide-details
              dense
            />
            <div class="caption grey--text payroll-member__note">Excluido de esta obligación</div>
          </div>
          <!-- Los no activos van abajo, tachados y sin poder tocarlos: no se
               informan, pero se ve si figuraban. -->
          <v-checkbox
            v-for="member in section.inactive"
            :key="member._id"
            v-model="selected"
            :value="member._id"
            :label="section.labelOf(member)"
            class="payroll-member--inactive"
            disabled
            hide-details
            dense
          />
        </div>
        <v-alert v-if="errorMessage" type="error" dense text class="mt-4">{{ errorMessage }}</v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn :disabled="saving" @click="$emit('close')">{{ resx.labels.cancel }}</v-btn>
        <v-btn v-if="!lockMessage" class="primary" :loading="saving" @click="save">{{ resx.labels.ok }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
"use strict";

import contractorCompanyService from "@/services/ContractorCompanyService";
import { payrollMembers, isExcludedFrom } from "@/utils/payroll";

const employeeLabel = e =>
  [`${e.lastName}, ${e.firstName}`, e.identifier && e.identifier.number]
    .filter(Boolean)
    .join(" - ");
const vehicleLabel = v => [v.uniqueIdentifier, v.description].filter(Boolean).join(" - ");

const compareText = (a, b) => (a || "").localeCompare(b || "", "es", { sensitivity: "base" });
const byLastName = (a, b) => compareText(a.lastName, b.lastName) || compareText(a.firstName, b.firstName);
const byLabel = (a, b) => compareText(vehicleLabel(a), vehicleLabel(b));
const sorted = (members, compare) => (members || []).slice().sort(compare);

export default {
  name: "obligation-payroll-dialog",
  props: {
    obligation: { type: Object, required: true },
    contractorCompanyId: { type: String, required: true },
    // La contratista con sus empleados y vehiculos.
    contractor: { type: Object, required: true },
    // { employees, vehicles }: a quien cubren las etiquetas de nomina.
    scope: { type: Object, required: true },
    // Por que no se puede tildar (vencida o sin cargar): de solo lectura.
    lockMessage: { type: String, default: null }
  },
  data() {
    return {
      selected: (this.obligation.coveredReferences || []).map(String),
      saving: false,
      errorMessage: null
    };
  },
  computed: {
    sections() {
      const sections = [];
      const splitExcluded = (members) => Object.assign(members, {
        active: members.active.filter(m => !isExcludedFrom(this.obligation, m)),
        excludedHere: members.active.filter(m => isExcludedFrom(this.obligation, m))
      });
      if (this.scope.employees) {
        sections.push(Object.assign(
          { kind: "employees", title: "Personal", empty: "La contratista no tiene personal cargado.", labelOf: employeeLabel },
          splitExcluded(payrollMembers(sorted(this.contractor.employees, byLastName)))
        ));
      }
      if (this.scope.vehicles) {
        sections.push(Object.assign(
          { kind: "vehicles", title: "Vehículos", empty: "La contratista no tiene vehículos cargados.", labelOf: vehicleLabel },
          splitExcluded(payrollMembers(sorted(this.contractor.vehicles, byLabel)))
        ));
      }
      return sections;
    }
  },
  methods: {
    selectAll(section, checked) {
      const ids = section.active.map(m => m._id);
      const others = this.selected.filter(id => ids.indexOf(id) === -1);
      this.selected = checked ? others.concat(ids) : others;
    },
    messageOf(error, fallback) {
      return (error.response && error.response.data && error.response.data.message) || fallback;
    },
    async save() {
      if (this.saving) return;
      this.errorMessage = null;
      this.saving = true;
      try {
        await contractorCompanyService.setObligationCoverage(
          this.contractorCompanyId,
          this.obligation,
          this.selected
        );
        this.$emit("saved", this.selected);
        this.$emit("close");
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudo guardar la nómina");
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style lang="scss">
.payroll-section + .payroll-section {
  margin-top: 20px;
}
.payroll-member__note {
  padding-left: 32px;
}
.payroll-member--inactive {
  opacity: 0.6;
  .v-label {
    text-decoration: line-through;
    color: rgba(0, 0, 0, 0.45) !important;
  }
}
</style>
